import { useTranslation } from '@pancakeswap/localization'
import { AddIcon, Box, Button, Flex, FlexGap, IconButton, MinusIcon, useModal, useToast } from '@pancakeswap/uikit'
import { FarmWidget } from '@pancakeswap/widgets-internal'
import { ToastDescriptionWithTx } from 'components/Toast'
import useCatchTxError from 'hooks/useCatchTxError'
import BCakeCalculator from 'views/Farms/components/YieldBooster/components/BCakeCalculator'
import { useCallback, useContext, useState, useMemo } from 'react'
import { styled } from 'styled-components'
import { useRouter } from 'next/router'
import { useFarmFromPid, useLpTokenPrice } from 'state/farms/hooks'
import { useCakePrice } from 'hooks/useCakePrice'
import { useAppDispatch } from 'state'
import { WNATIVE, NATIVE } from '@pancakeswap/sdk'
import { ChainId } from '@pancakeswap/chains'
import { SendTransactionResult } from 'wagmi/actions'
import BigNumber from 'bignumber.js'
import { useIsBloctoETH } from 'views/Farms'
import { DEFAULT_TOKEN_DECIMAL } from 'config'
import useNativeCurrency from 'hooks/useNativeCurrency'
import { formatLpBalance } from '@pancakeswap/utils/formatBalance'
import { pickFarmTransactionTx } from 'state/global/actions'
import { useTransactionAdder, useNonBscFarmPendingTransaction } from 'state/transactions/hooks'
import { FarmTransactionStatus, NonBscFarmStepType } from 'state/transactions/actions'
import WalletModal, { WalletView } from 'components/EMenu/UserMenu/WalletModal'
import { FarmWithStakedValue } from '@pancakeswap/farms'
import useAccountActiveChain from 'hooks/useAccountActiveChain'
import { YieldBoosterStateContext } from '../YieldBooster/components/ProxyFarmContainer'
import { YieldBoosterState } from '../YieldBooster/hooks/useYieldBoosterState'
import { useFirstTimeCrossFarming } from '../../hooks/useFirstTimeCrossFarming'

interface FarmCardActionsProps extends FarmWithStakedValue {
  lpLabel?: string
  addLiquidityUrl?: string
  displayApr?: string
  onStake?: (value: string) => Promise<SendTransactionResult>
  onUnstake?: (value: string) => Promise<SendTransactionResult>
  onDone?: () => void
  onApprove?: () => Promise<SendTransactionResult>
  isApproved?: boolean
}

const IconButtonWrapper = styled.div`
  display: flex;
  // svg {
  //   width: 20px;
  // }
`

const StyledButton = styled(Button)`
  font-family: inherit;
  font-size: 16px;
  border-radius: 24px;
  padding: 12px 24px;
  height: fit-content;
  margin-top: 6px;
`

const StakeAction: React.FC<React.PropsWithChildren<FarmCardActionsProps>> = ({
  pid,
  vaultPid,
  quoteToken,
  token,
  lpSymbol,
  lpTokenPrice,
  multiplier,
  apr,
  lpAddress,
  displayApr,
  addLiquidityUrl,
  lpLabel,
  lpTotalSupply,
  tokenAmountTotal,
  quoteTokenAmountTotal,
  userData,
  onStake,
  onUnstake,
  onDone,
  onApprove,
  isApproved,
}) => {
  const { t } = useTranslation()
  const { pathname } = useRouter()
  const isPRC20 = pathname.includes('prc20farms')
  const dispatch = useAppDispatch()
  const addTransaction = useTransactionAdder()
  const { account, chainId } = useAccountActiveChain()
  const native = useNativeCurrency()
  const { tokenBalance, stakedBalance, allowance } = userData
  const cakePrice = useCakePrice()
  const router = useRouter()
  const { lpTokenStakedAmount } = useFarmFromPid(pid)
  const { toastSuccess } = useToast()
  const { fetchWithCatchTxError, fetchTxResponse, loading: pendingTx } = useCatchTxError()
  const { boosterState } = useContext(YieldBoosterStateContext)
  const [bCakeMultiplier, setBCakeMultiplier] = useState<number | null>(() => null)
  const pendingFarm = useNonBscFarmPendingTransaction(lpAddress)
  const { isFirstTime, refresh: refreshFirstTime } = useFirstTimeCrossFarming(vaultPid)
  const isBloctoETH = useIsBloctoETH()

  const crossChainWarningText = useMemo(() => {
    return isFirstTime
      ? t('A small amount of %nativeToken% is required for the first-time setup of cross-chain BBC farming.', {
          nativeToken: native.symbol,
        })
      : t('For safety, cross-chain transactions will take around 30 minutes to confirm.')
  }, [isFirstTime, native, t])

  const isStakeReady = useMemo(() => {
    return ['history', 'archived'].some((item) => router.pathname.includes(item)) || pendingFarm.length > 0
  }, [pendingFarm, router])

  const handleStake = async (amount: string) => {
    if (vaultPid) {
      await handleNonBscStake(amount)
      refreshFirstTime()
    } else {
      const receipt = await fetchWithCatchTxError(() => onStake(amount), {
        type: 'stake-farm',
        summary: `Staked ${amount} ${lpSymbol} to ${isPRC20 ? "PRC20" : ""} farm`,
        translatableSummary: {
          text: `Staked %amount% %lpSymbol% LP to ${isPRC20 ? "PRC20" : ""} farm`,
          data: {
            amount, lpSymbol 
          }
        }
      })
      if (receipt?.status) {
        toastSuccess(
          `${t('Staked')}!`,
          <ToastDescriptionWithTx txHash={receipt.transactionHash}>
            {t('Your funds have been staked in the farm')}
          </ToastDescriptionWithTx>,
        )
        onDone()
      }
    }
  }

  const handleNonBscStake = async (amountValue: string) => {
    const receipt = await fetchTxResponse(() => onStake(amountValue))
    const amountAsBigNumber = new BigNumber(amountValue).times(DEFAULT_TOKEN_DECIMAL)
    const amount = formatLpBalance(new BigNumber(amountAsBigNumber), 18)

    if (receipt) {
      addTransaction(receipt, {
        type: 'non-bsc-farm',
        translatableSummary: {
          text: 'Stake %amount% %lpSymbol% Token',
          data: { amount, lpSymbol },
        },
        nonBscFarm: {
          type: NonBscFarmStepType.STAKE,
          status: FarmTransactionStatus.PENDING,
          amount,
          lpSymbol,
          lpAddress,
          steps: [
            {
              step: 1,
              chainId,
              tx: receipt.hash,
              isFirstTime,
              status: FarmTransactionStatus.PENDING,
            },
            {
              step: 2,
              tx: '',
              chainId: ChainId.BSC,
              status: FarmTransactionStatus.PENDING,
            },
          ],
        },
      })

      dispatch(pickFarmTransactionTx({ tx: receipt.hash, chainId }))
      onDone()
    }
  }

  const handleUnstake = async (amount: string) => {
    if (vaultPid) {
      await handleNonBscUnStake(amount)
    } else {
      const receipt = await fetchWithCatchTxError(() => onUnstake(amount), {
        type: 'unstake-farm',
        summary: `Unstaked ${amount} ${lpSymbol} from ${isPRC20 ? "PRC20" : ""} farm`,
        translatableSummary: {
          text: `Unstaked %amount% %lpSymbol% from ${isPRC20 ? "PRC20" : ""} farm`,
          data: {
            amount, lpSymbol 
          }
        }
      })
      if (receipt?.status) {
        toastSuccess(
          `${t('Unstaked')}!`,
          <ToastDescriptionWithTx txHash={receipt.transactionHash}>
            {t('Your earnings have also been harvested to your wallet')}
          </ToastDescriptionWithTx>,
        )
        onDone()
      }
    }
  }

  const handleNonBscUnStake = async (amountValue: string) => {
    const receipt = await fetchTxResponse(() => onUnstake(amountValue))
    const amountAsBigNumber = new BigNumber(amountValue).times(DEFAULT_TOKEN_DECIMAL)
    const amount = formatLpBalance(new BigNumber(amountAsBigNumber), 18)

    if (receipt) {
      addTransaction(receipt, {
        type: 'non-bsc-farm',
        translatableSummary: {
          text: 'Unstake %amount% %lpSymbol% Token',
          data: { amount, lpSymbol },
        },
        nonBscFarm: {
          type: NonBscFarmStepType.UNSTAKE,
          status: FarmTransactionStatus.PENDING,
          amount,
          lpSymbol,
          lpAddress,
          steps: [
            {
              step: 1,
              chainId,
              tx: receipt.hash,
              status: FarmTransactionStatus.PENDING,
            },
            {
              step: 2,
              chainId: ChainId.BSC,
              tx: '',
              status: FarmTransactionStatus.PENDING,
            },
            {
              step: 3,
              chainId,
              tx: '',
              status: FarmTransactionStatus.PENDING,
            },
          ],
        },
      })

      dispatch(pickFarmTransactionTx({ tx: receipt.hash, chainId }))
      onDone()
    }
  }

  const handleApprove = useCallback(async () => {
    const receipt = await fetchWithCatchTxError(() => onApprove(), {
      type: 'approve',
      summary: `Enable ${lpSymbol} ${isPRC20 ? "PRC20" : ""} farm`,
      translatableSummary: {
        text: `Enable %lpSymbol% ${isPRC20 ? "PRC20" : ""} farm`,
        data: {
          lpSymbol 
        }
      }
    })
    if (receipt?.status) {
      // toastSuccess(t('Contract Enabled'), <ToastDescriptionWithTx txHash={receipt.transactionHash} />)
      onDone()
    }
  }, [onApprove, lpSymbol, isPRC20, fetchWithCatchTxError, onDone])

  const bCakeCalculatorSlot = (calculatorBalance) => (
    <BCakeCalculator
      targetInputBalance={calculatorBalance}
      earningTokenPrice={cakePrice.toNumber()}
      lpTokenStakedAmount={lpTokenStakedAmount}
      setBCakeMultiplier={setBCakeMultiplier}
    />
  )

  const [onPresentDeposit] = useModal(
    <FarmWidget.DepositModal
      account={account}
      pid={pid}
      lpTotalSupply={lpTotalSupply}
      max={tokenBalance}
      stakedBalance={stakedBalance}
      tokenName={lpSymbol}
      multiplier={multiplier}
      lpPrice={lpTokenPrice}
      lpLabel={lpLabel}
      apr={apr}
      displayApr={displayApr}
      addLiquidityUrl={addLiquidityUrl}
      cakePrice={cakePrice}
      showActiveBooster={boosterState === YieldBoosterState.ACTIVE}
      bCakeMultiplier={bCakeMultiplier}
      showCrossChainFarmWarning={false}
      crossChainWarningText={crossChainWarningText}
      decimals={18}
      allowance={allowance}
      enablePendingTx={pendingTx}
      onConfirm={handleStake}
      handleApprove={handleApprove}
      bCakeCalculatorSlot={bCakeCalculatorSlot}
    />,
    true,
    true,
    `farm-deposit-modal-${pid}`,
  )

  const [onPresentWithdraw] = useModal(
    <FarmWidget.WithdrawModal
      showActiveBooster={boosterState === YieldBoosterState.ACTIVE}
      max={stakedBalance}
      onConfirm={handleUnstake}
      lpPrice={lpTokenPrice}
      tokenName={lpSymbol}
      showCrossChainFarmWarning={false}
      decimals={18}
    />,
  )

  const renderStakingButtons = () => {
    return stakedBalance.eq(0) ? (
      <StyledButton variant="secondary" onClick={onPresentDeposit} disabled={isStakeReady} style={{ width: "100%" }}>
        {t('Stake LP')}
      </StyledButton>
    ) : (
      <FlexGap gap="16px" justifyContent="space-between">
        <StyledButton disabled={pendingFarm.length > 0} variant="secondary" onClick={onPresentWithdraw} style={{ flex: 1 }}>
          {t("Pull out")}
        </StyledButton>
        <StyledButton onClick={onPresentDeposit} variant="secondary" disabled={isStakeReady || isBloctoETH} style={{ flex: 1 }}>
          {t("Insert")}
        </StyledButton>
      </FlexGap>
    )
  }

  const [onPresentTransactionModal] = useModal(<WalletModal initialView={WalletView.TRANSACTIONS} />)

  const onClickLoadingIcon = () => {
    const { length } = pendingFarm
    if (length) {
      if (length > 1) {
        onPresentTransactionModal()
      } else {
        dispatch(pickFarmTransactionTx({ tx: pendingFarm[0].txid, chainId }))
      }
    }
  }

  // TODO: Move this out to prevent unnecessary re-rendered
  if (!isApproved) {
    return (
      <StyledButton mt="8px" width="100%" disabled={pendingTx || isBloctoETH} onClick={handleApprove}>
        {t('Enable Contract')}
      </StyledButton>
    )
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  // const lpTokenPriceAlt = useLpTokenPrice(lpSymbol)
  const lpTokenPriceAlt = lpTokenPrice
  return (
    <>
      <FarmWidget.StakedLP
        decimals={18}
        stakedBalance={stakedBalance}
        quoteTokenSymbol={WNATIVE[chainId]?.symbol === quoteToken.symbol ? NATIVE[chainId]?.symbol : quoteToken.symbol}
        tokenSymbol={WNATIVE[chainId]?.symbol === token.symbol ? NATIVE[chainId]?.symbol : token.symbol}
        lpTotalSupply={lpTotalSupply}
        lpTokenStakedAmount={lpTokenStakedAmount}
        lpTokenPrice={lpTokenPriceAlt}
        tokenAmountTotal={tokenAmountTotal}
        quoteTokenAmountTotal={quoteTokenAmountTotal}
        pendingFarmLength={pendingFarm.length}
        onClickLoadingIcon={onClickLoadingIcon}
      />
      <Box mt="1em">
        {renderStakingButtons()}
      </Box>
    </>
  )
}

export default StakeAction
